import {
  Group,
  Pagination,
  Paper,
  Stack,
  Table,
  Tabs,
  TextInput,
  Title,
} from '@mantine/core';
import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { urlField } from '@/lib/urlState';
import { useAccountsList, type AccountListFilters } from '@/models/accounts';
import { formatDate } from '@/lib/helpers';
import { useHasAnyPermission } from '@/models/permissions';
import { OwnerFilter } from '@/components/OwnerFilter/OwnerFilter';

export default function AccountsListPage() {
  const canReadAll = useHasAnyPermission([
    'contacts:read-all',
    'contacts:*',
    '*:*',
  ]);

  const schema = useMemo(
    () => ({
      view: canReadAll
        ? urlField.enum(['mine', 'all'] as const, 'all')
        : urlField.enum(['mine'] as const, 'mine'),
      search: urlField.string(),
      owners: urlField.numberArray(),
      page: urlField.page(),
      pageSize: urlField.number(50, { integer: true, min: 1, max: 200 }),
      sort: urlField.enum(
        ['createdAt', 'updatedAt', 'name'] as const,
        'createdAt',
      ),
      order: urlField.enum(['asc', 'desc'] as const, 'desc'),
    }),
    [canReadAll],
  );
  const [state, setState, { clear, isFiltered }] = useUrlState(schema);
  const [searchInput, setSearchInput] = useUrlDraft(state.search, (search) =>
    setState({ search }),
  );
  const filters: AccountListFilters = {
    view: state.view,
    search: state.search || undefined,
    ownerUserId: state.owners.length ? state.owners : undefined,
    page: state.page,
    pageSize: state.pageSize,
    sort: state.sort,
    order: state.order,
  };
  const { data, isLoading } = useAccountsList(filters);
  const [, navigate] = useLocation();

  return (
    <Paper p="md" h="100%">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Accounts</Title>
        </Group>

        <Tabs
          value={state.view}
          onChange={(v) => v && setState({ view: v as typeof state.view })}
        >
          <Tabs.List>
            <Tabs.Tab value="mine">My accounts</Tabs.Tab>
            {canReadAll && <Tabs.Tab value="all">All</Tabs.Tab>}
          </Tabs.List>
        </Tabs>

        <Group>
          <TextInput
            placeholder="Search by name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            w={280}
          />
          <OwnerFilter
            size="sm"
            value={state.owners}
            onChange={(owners) => setState({ owners })}
            w={220}
          />
          <ClearFiltersButton visible={isFiltered} onClick={clear} />
        </Group>

        <Table
          highlightOnHover
          fz="xs"
          styles={{
            td: { whiteSpace: 'nowrap' },
            th: { whiteSpace: 'nowrap' },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Contacts</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data?.rows ?? []).map((r) => (
              <Table.Tr
                key={r.id}
                onClick={() => navigate(`~/accounts/${r.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>{r.name}</Table.Td>
                <Table.Td>{r.contactsCount}</Table.Td>
                <Table.Td>{formatDate(r.createdAt)}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && (data?.rows ?? []).length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={3} align="center">
                  No accounts
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        <Group justify="flex-end">
          <Pagination
            total={Math.max(1, Math.ceil((data?.total ?? 0) / state.pageSize))}
            value={state.page}
            onChange={(page) => setState({ page })}
          />
        </Group>
      </Stack>
    </Paper>
  );
}
